import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CANVAS_COLOR_VARS, getCanvasChartColors, getCanvasColor } from '../canvas-theme.utils';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { CatalogueApiService, RsApiService } from '@angular-workspace/services';
import { ChartsDatasetsModalComponent } from './charts-datasets-modal.component';
import {
  CanvasDataset,
  extractCatalogueItemRecord,
  organizationNameFromCatalogueItem,
} from '../canvas-catalogue.util';
import { CANVAS_DATASET_DETAIL_URL } from '../../canvas-tokens';
import {
  ChartsDateRangePickerComponent,
  DateRange,
} from './date-range-picker/charts-date-range-picker.component';

export type ChartKind = 'line' | 'bar' | 'pie';
export type TimeFilter = '24h' | '7d' | '30d' | 'custom';

export interface ChartDataset {
  id: string;
  name: string;
  fullName: string;
  organizationName: string;
}

export interface PlotSchema {
  plotType: 'barPlot' | 'linePlot' | 'multiLinePlot';
  xAxis: string[];
  yAxis: string[];
  dynamic: string[];
}

/** Versioned chart export file — suitable for re-import. */
export interface ChartExportPayload {
  version: 1;
  exportedAt: string;
  dataset: { id: string; name: string };
  chart: {
    plotType: string;
    chartTypeLabel: string;
    xField: string;
    yField: string;
    activeSchemaIndex: number;
  };
  timeFilter: TimeFilter;
  timeAt: string;
  endTimeAt: string;
  plotSchema: PlotSchema[];
  data: Record<string, unknown>[];
}

const CHART_COLORS = () => getCanvasChartColors();

/** Minimal ECharts surface used for PNG/JPEG export (avoids ECharts vs EChartsType mismatch). */
interface ChartExportInstance {
  getDataURL(opts: {
    type?: 'png' | 'jpeg' | 'svg';
    pixelRatio?: number;
    backgroundColor?: string;
  }): string;
  isDisposed(): boolean;
}

const PLOT_TYPE_CONFIG: Record<PlotSchema['plotType'], { kind: 'bar' | 'line'; label: string }> = {
  barPlot: { kind: 'bar', label: 'Bar Chart' },
  linePlot: { kind: 'line', label: 'Line Chart' },
  multiLinePlot: { kind: 'line', label: 'Multi-Line Chart' },
};

function getValueCI(obj: Record<string, unknown>, key: string): unknown {
  if (key in obj) return obj[key];
  const lowerKey = key.toLowerCase();
  const match = Object.keys(obj).find((k) => k.toLowerCase() === lowerKey);
  return match !== undefined ? obj[match] : undefined;
}

function toNumeric(v: unknown): number | null {
  if (typeof v === 'number') return isNaN(v) ? null : v;
  if (v == null) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

function formatChartDate(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day}-${monthNames[date.getMonth()]}-${date.getFullYear()}`;
}

function formatYmd(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toApiPropertyName(field: string): string {
  if (!field) return field;
  return field.charAt(0).toLowerCase() + field.slice(1);
}

/** Format date as YYYY-MM-DDTHH:mm:ss+05:30 (IST), matching the temporal API. */
function toTemporalDatetime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const istMs = date.getTime() + 5.5 * 60 * 60 * 1000;
  const ist = new Date(istMs);
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}T${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}:${pad(ist.getUTCSeconds())}+05:30`;
}

function formatXValue(val: unknown): string {
  if (typeof val === 'string') {
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
      }
    } catch { /* fall through */ }
  }
  return String(val ?? '');
}

@Component({
  selector: 'lib-charts-home',
  standalone: true,
  imports: [CommonModule, FormsModule, ChartsDatasetsModalComponent, NgxEchartsDirective, ChartsDateRangePickerComponent],
  templateUrl: './charts-home.component.html',
  styleUrl: './charts-home.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ChartsHomeComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly rsApi = inject(RsApiService);
  private readonly datasetDetailUrl =
    inject(CANVAS_DATASET_DETAIL_URL, { optional: true }) ??
    ((id: string) => `/dataset/${id}`);

  /** Live ECharts instance — set via (chartInit) on the chart container. */
  private chartInstance: ChartExportInstance | null = null;

  // plotSchema is fetched once per dataset id; entity data is cached per (id + time range)
  private readonly plotSchemaCache = new Map<string, PlotSchema[]>();
  private readonly entityDataCache = new Map<string, Record<string, unknown>[]>();

  readonly searchQuery = signal('');
  readonly selectedId = signal<string | null>(null);
  readonly isDatasetsModalOpen = signal(false);

  // ── Chart data ────────────────────────────────────────────────────────────
  readonly plotSchema = signal<PlotSchema[]>([]);
  readonly entityData = signal<Record<string, unknown>[]>([]);
  readonly chartLoading = signal(false);
  readonly activeSchemaIndex = signal(0);
  readonly selectedYField = signal<string | null>(null);

  readonly activeSchema = computed(() => {
    const schemas = this.plotSchema();
    return schemas[this.activeSchemaIndex()] ?? null;
  });

  readonly availableChartTypes = computed(() =>
    this.plotSchema().map((schema, idx) => ({
      idx,
      kind: PLOT_TYPE_CONFIG[schema.plotType]?.kind ?? 'bar',
      label: PLOT_TYPE_CONFIG[schema.plotType]?.label ?? 'Chart',
      plotType: schema.plotType,
    })),
  );

  readonly yAxisOptions = computed(() => {
    const schema = this.activeSchema();
    if (!schema) return [];
    return [...schema.yAxis, ...schema.dynamic];
  });

  readonly currentYFieldLabel = computed(() => {
    const schema = this.activeSchema();
    if (!schema) return 'Y-Axis';
    if (schema.plotType === 'multiLinePlot') return 'All Fields';
    return this.selectedYField() ?? schema.yAxis[0] ?? 'Y-Axis';
  });

  readonly xAxisLabel = computed(() => this.activeSchema()?.xAxis[0] ?? 'X-Axis');

  readonly chartTypeLabel = computed(() => {
    const schema = this.activeSchema();
    if (!schema) return 'Chart Type';
    return PLOT_TYPE_CONFIG[schema.plotType]?.label ?? 'Chart Type';
  });

  readonly echartsOption = computed<EChartsOption | null>(() => {
    const schema = this.activeSchema();
    const data = this.entityData();
    if (!schema || data.length === 0) return null;

    const xField = schema.xAxis[0];
    const isMultiLine = schema.plotType === 'multiLinePlot';

    const xValues = data.map((row) => formatXValue(getValueCI(row, xField)));

    let series: object[];
    if (isMultiLine) {
      const yFields = [...schema.yAxis, ...schema.dynamic];
      series = yFields.map((field, i) => ({
        name: field,
        type: 'line',
        data: data.map((row) => toNumeric(getValueCI(row, field))),
        smooth: true,
        lineStyle: { width: 2 },
        symbol: 'circle',
        symbolSize: 4,
        color: CHART_COLORS()[i % CHART_COLORS().length],
      }));
    } else {
      const yField = this.selectedYField() ?? schema.yAxis[0];
      const echartsType = schema.plotType === 'barPlot' ? 'bar' : 'line';
      series = [{
        name: yField,
        type: echartsType,
        data: data.map((row) => toNumeric(getValueCI(row, yField))),
        smooth: echartsType === 'line',
        color: CHART_COLORS()[0],
        ...(echartsType === 'bar' ? { barMinHeight: 3 } : {}),
      }];
    }

    const showLegend = isMultiLine;
    const labelInterval = Math.max(0, Math.floor(xValues.length / 8) - 1);
    const sliderHeight = 22;
    const sliderBottom = showLegend ? 48 : 8;
    const gridBottom = showLegend ? 128 : 88;

    return {
      tooltip: { trigger: 'axis', confine: true },
      legend: showLegend
        ? { bottom: 0, type: 'scroll', textStyle: { fontSize: 11 } }
        : { show: false },
      grid: {
        left: 16,
        right: 16,
        top: 20,
        bottom: gridBottom,
        containLabel: true,
      },
      dataZoom: [
        { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: true, moveOnMouseMove: true },
        { type: 'inside', yAxisIndex: 0, zoomOnMouseWheel: 'shift', moveOnMouseMove: false },
        {
          type: 'slider',
          xAxisIndex: 0,
          bottom: sliderBottom,
          height: sliderHeight,
          backgroundColor: getCanvasColor(CANVAS_COLOR_VARS.chartZoomBg),
          borderColor: getCanvasColor(CANVAS_COLOR_VARS.chartZoomBorder),
          fillerColor: getCanvasColor(CANVAS_COLOR_VARS.chartZoomFiller),
          handleSize: 12,
          moveHandleSize: 4,
          handleStyle: { color: getCanvasColor(CANVAS_COLOR_VARS.chartBrand), borderWidth: 0 },
          moveHandleStyle: {
            color: getCanvasColor(CANVAS_COLOR_VARS.chartBrand),
            borderWidth: 0,
            opacity: 0.55,
          },
          textStyle: { fontSize: 10, color: getCanvasColor(CANVAS_COLOR_VARS.textSecondary) },
          showDetail: false,
          showDataShadow: true,
          dataBackground: {
            lineStyle: { color: getCanvasColor(CANVAS_COLOR_VARS.chartDataLine), width: 1 },
            areaStyle: { color: getCanvasColor(CANVAS_COLOR_VARS.chartDataArea) },
          },
          selectedDataBackground: {
            lineStyle: { color: getCanvasColor(CANVAS_COLOR_VARS.chartAxisLine), width: 1 },
            areaStyle: { color: getCanvasColor(CANVAS_COLOR_VARS.chartZoomSelected) },
          },
        },
      ],
      xAxis: {
        type: 'category',
        data: xValues,
        axisLabel: {
          rotate: 35,
          fontSize: 11,
          color: getCanvasColor(CANVAS_COLOR_VARS.chartNameText),
          fontWeight: 500,
          interval: labelInterval,
          margin: 10,
          show: true,
        },
        axisTick: { show: true, lineStyle: { color: getCanvasColor(CANVAS_COLOR_VARS.chartAxisLine) } },
        axisLine: { show: true, lineStyle: { color: getCanvasColor(CANVAS_COLOR_VARS.chartAxisLine) } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 11, color: getCanvasColor(CANVAS_COLOR_VARS.chartAxisLabel) },
        splitLine: { lineStyle: { color: getCanvasColor(CANVAS_COLOR_VARS.chartSplitLine) } },
      },
      series,
    } as EChartsOption;
  });

  // ── Y-axis dropdown ───────────────────────────────────────────────────────
  readonly isYAxisOpen = signal(false);

  toggleYAxisOpen(): void {
    if (this.activeSchema()?.plotType === 'multiLinePlot') return;
    this.isYAxisOpen.update((v) => !v);
  }

  selectYField(field: string): void {
    this.selectedYField.set(field);
    this.isYAxisOpen.set(false);
    this.refetchIfSelected();
  }

  // ── Time filter ───────────────────────────────────────────────────────────
  readonly selectedTimeFilter = signal<TimeFilter>('7d');
  readonly customDateRange = signal<DateRange>({ startDate: null, endDate: null });
  readonly isTimeFilterOpen = signal(false);

  readonly timeFilterLabel = computed(() => {
    switch (this.selectedTimeFilter()) {
      case '24h': return 'Last 24 Hrs';
      case '7d':  return 'Last Week';
      case '30d': return 'Last Month';
      case 'custom': {
        const { startDate, endDate } = this.customDateRange();
        if (startDate && endDate) {
          return `${formatChartDate(startDate)} to ${formatChartDate(endDate)}`;
        }
        return 'Custom Range';
      }
    }
  });

  readonly canApplyCustomRange = computed(() => {
    const { startDate, endDate } = this.customDateRange();
    return Boolean(startDate) && Boolean(endDate);
  });

  toggleTimeFilterOpen(): void {
    this.isTimeFilterOpen.update((v) => !v);
  }

  /** IST datetime string for the temporal API, e.g. 2026-06-24T11:41:00+05:30 */
  private getTimeRange(): { timeAt: string; endTimeAt: string } {
    const filter = this.selectedTimeFilter();
    const now = new Date();

    if (filter === '24h') {
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return { timeAt: toTemporalDatetime(start), endTimeAt: toTemporalDatetime(now) };
    }
    if (filter === '7d') {
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { timeAt: toTemporalDatetime(start), endTimeAt: toTemporalDatetime(now) };
    }
    if (filter === '30d') {
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { timeAt: toTemporalDatetime(start), endTimeAt: toTemporalDatetime(now) };
    }
    // custom — interpret selected dates in IST
    const { startDate, endDate } = this.customDateRange();
    return {
      timeAt: startDate
        ? toTemporalDatetime(new Date(`${formatYmd(startDate)}T00:00:00+05:30`))
        : toTemporalDatetime(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)),
      endTimeAt: endDate
        ? toTemporalDatetime(new Date(`${formatYmd(endDate)}T23:59:59+05:30`))
        : toTemporalDatetime(now),
    };
  }

  selectTimeFilter(filter: TimeFilter): void {
    this.selectedTimeFilter.set(filter);
    if (filter !== 'custom') {
      this.isTimeFilterOpen.set(false);
      this.refetchIfSelected();
    }
  }

  applyCustomRange(): void {
    if (!this.canApplyCustomRange()) return;
    this.isTimeFilterOpen.set(false);
    this.refetchIfSelected();
  }

  onCustomDateRangeChange(range: DateRange | null): void {
    const next = range ?? { startDate: null, endDate: null };
    this.customDateRange.set(next);
    if (next.startDate && next.endDate) {
      this.applyCustomRange();
    }
  }

  // ── Chart type dropdown ───────────────────────────────────────────────────
  readonly isChartTypeOpen = signal(false);

  toggleChartTypeOpen(): void {
    if (this.availableChartTypes().length === 0) return;
    this.isChartTypeOpen.update((v) => !v);
  }

  selectChartKind(idx: number): void {
    this.activeSchemaIndex.set(idx);
    this.selectedYField.set(null);
    this.isChartTypeOpen.set(false);
    this.refetchIfSelected();
  }

  // ── Dataset management ────────────────────────────────────────────────────
  private readonly datasets = signal<ChartDataset[]>([]);

  readonly hasDatasets = computed(() => this.datasets().length > 0);

  readonly selectedDataset = computed(() =>
    this.datasets().find((d) => d.id === this.selectedId()) ?? null,
  );

  readonly filteredDatasets = computed(() => {
    const list = this.datasets();
    const q = this.searchQuery().toLowerCase().trim();
    return q ? list.filter((d) => d.name.toLowerCase().includes(q)) : list;
  });

  openDatasetsModal(): void {
    this.isDatasetsModalOpen.set(true);
  }

  async selectDataset(id: string): Promise<void> {
    this.selectedId.set(id);
    await this.loadChartData(id);
  }

  async onDatasetSelected(dataset: CanvasDataset): Promise<void> {
    const already = this.datasets().some((d) => d.id === dataset.id);
    if (!already) {
      this.datasets.update((arr) => [
        ...arr,
        {
          id: dataset.id,
          name: dataset.name,
          fullName: dataset.name,
          organizationName: dataset.provider,
        },
      ]);
    }
    this.selectedId.set(dataset.id);
    await this.loadChartData(dataset.id);
  }

  private async loadChartData(id: string): Promise<void> {
    this.chartLoading.set(true);
    this.entityData.set([]);

    const cachedSchema = this.plotSchemaCache.get(id);
    if (!cachedSchema) {
      this.plotSchema.set([]);
      this.activeSchemaIndex.set(0);
      this.selectedYField.set(null);
    }

    try {
      // Step 1: fetch or reuse schema
      let schemas: PlotSchema[];
      if (cachedSchema) {
        schemas = cachedSchema;
      } else {
        const itemResponse = await this.catalogueApi.getItemById(id);
        const item = extractCatalogueItemRecord(itemResponse);
        schemas = (item?.['plotSchema'] as PlotSchema[]) ?? [];
        this.setDatasetOrganization(id, organizationNameFromCatalogueItem(item));
        this.plotSchemaCache.set(id, schemas);
        this.plotSchema.set(schemas);
        this.activeSchemaIndex.set(0);
        this.selectedYField.set(null);
      }

      const schema = schemas[this.activeSchemaIndex()] ?? schemas[0];
      if (!schema) {
        return;
      }

      // Step 2: build temporal API params from plotSchema
      // timeproperty = xAxis (camelCase for API); pick omitted for multi-line charts
      const timeproperty = toApiPropertyName(schema.xAxis[0]);
      const pick = this.resolvePickField(schema);

      const { timeAt, endTimeAt } = this.getTimeRange();
      const entityKey = `${id}|${timeAt}|${endTimeAt}|${timeproperty}|${pick ?? '__all__'}`;

      const cachedData = this.entityDataCache.get(entityKey);
      if (cachedData) {
        this.entityData.set(cachedData);
        return;
      }

      // Step 3: fetch temporal entities
      const entitiesResponse = await this.rsApi.temporalEntitiesNgsiLd({
        id,
        timeAt,
        endTimeAt,
        timeproperty,
        ...(pick ? { pick } : {}),
      });

      const data: Record<string, unknown>[] =
        (entitiesResponse?.result as Record<string, unknown>[]) ??
        (Array.isArray(entitiesResponse) ? entitiesResponse : []);

      this.entityDataCache.set(entityKey, data);
      this.entityData.set(data);
    } catch {
      // NetworkService surfaces the error via its own toaster
    } finally {
      this.chartLoading.set(false);
    }
  }

  private setDatasetOrganization(id: string, organizationName: string): void {
    if (!organizationName) return;
    this.datasets.update((arr) =>
      arr.map((d) => (d.id === id ? { ...d, organizationName } : d)),
    );
  }

  /** Re-fetch entity data for the currently selected dataset. */
  private refetchIfSelected(): void {
    const id = this.selectedId();
    if (id) void this.loadChartData(id);
  }

  /** pick = yAxis field for bar/line; omitted (undefined) for multi-line. */
  private resolvePickField(schema: PlotSchema): string | undefined {
    if (schema.plotType === 'multiLinePlot') return undefined;
    return this.selectedYField() ?? schema.yAxis[0] ?? undefined;
  }

  openDatasetInCatalogue(d: ChartDataset, ev: MouseEvent): void {
    ev.stopPropagation();
    const url = this.datasetDetailUrl(d.id);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  removeDatasetFromCharts(d: ChartDataset, ev: MouseEvent): void {
    ev.stopPropagation();
    this.plotSchemaCache.delete(d.id);
    for (const key of [...this.entityDataCache.keys()]) {
      if (key.startsWith(`${d.id}|`)) this.entityDataCache.delete(key);
    }
    this.datasets.update((arr) => arr.filter((x) => x.id !== d.id));
    if (this.selectedId() === d.id) {
      this.selectedId.set(null);
      this.plotSchema.set([]);
      this.entityData.set([]);
      this.isExportDropdownOpen.set(false);
    }
  }

  onSearch(e: Event): void {
    this.searchQuery.set((e.target as HTMLInputElement).value);
  }

  // ── Export chart dropdown ─────────────────────────────────────────────────
  readonly isExportDropdownOpen = signal(false);
  readonly exportFormat = signal<'json' | 'png' | 'jpeg'>('json');

  readonly canExportImage = computed(
    () => !this.chartLoading() && this.echartsOption() != null && this.chartInstance != null,
  );

  readonly canSubmitExport = computed(() => {
    const fmt = this.exportFormat();
    return fmt === 'json' || this.canExportImage();
  });

  onChartInit(chart: ChartExportInstance): void {
    this.chartInstance = chart;
  }

  onExportTriggerClick(): void {
    if (!this.selectedDataset()) return;
    this.isExportDropdownOpen.update((v) => !v);
  }

  selectExportFormat(key: 'json' | 'png' | 'jpeg'): void {
    if ((key === 'png' || key === 'jpeg') && !this.canExportImage()) return;
    this.exportFormat.set(key);
  }

  submitChartExport(): void {
    const ds = this.selectedDataset();
    if (!ds || !this.canSubmitExport()) return;

    const slug = ds.fullName.replace(/[^\w\-]+/g, '_').slice(0, 80) || 'chart';
    const fmt = this.exportFormat();

    if (fmt === 'json') {
      this.triggerDownload(
        new Blob([JSON.stringify(this.buildExportPayload(ds), null, 2)], {
          type: 'application/json',
        }),
        `${slug}-chart.json`,
      );
    } else if (fmt === 'png') {
      this.downloadChartImage('png', slug);
    } else {
      this.downloadChartImage('jpeg', slug);
    }

    this.isExportDropdownOpen.set(false);
  }

  private buildExportPayload(ds: ChartDataset): ChartExportPayload {
    const schema = this.activeSchema();
    const { timeAt, endTimeAt } = this.getTimeRange();
    return {
      version: 1,
      exportedAt: new Date().toISOString(),
      dataset: { id: ds.id, name: ds.fullName },
      chart: {
        plotType: schema?.plotType ?? '',
        chartTypeLabel: this.chartTypeLabel(),
        xField: this.xAxisLabel(),
        yField: this.currentYFieldLabel(),
        activeSchemaIndex: this.activeSchemaIndex(),
      },
      timeFilter: this.selectedTimeFilter(),
      timeAt,
      endTimeAt,
      plotSchema: this.plotSchema(),
      data: this.entityData(),
    };
  }

  private downloadChartImage(kind: 'png' | 'jpeg', slug: string): void {
    const chart = this.chartInstance;
    if (!chart || chart.isDisposed()) return;

    const dataUrl = chart.getDataURL({
      type: kind,
      pixelRatio: 2,
      backgroundColor: getCanvasColor(CANVAS_COLOR_VARS.chartBg),
    });

    const ext = kind === 'jpeg' ? 'jpg' : 'png';
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${slug}-chart.${ext}`;
    a.click();
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    const t = ev.target as HTMLElement | null;
    if (!t) return;

    if (
      this.isTimeFilterOpen() &&
      !t.closest('.ch-time-filter') &&
      !t.closest('.date-range-picker-calendar-overlay')
    ) {
      this.isTimeFilterOpen.set(false);
    }
    if (this.isChartTypeOpen() && !t.closest('.ch-chart-type')) {
      this.isChartTypeOpen.set(false);
    }
    if (this.isExportDropdownOpen() && !t.closest('.ch-export')) {
      this.isExportDropdownOpen.set(false);
    }
    if (this.isYAxisOpen() && !t.closest('.ch-y-axis')) {
      this.isYAxisOpen.set(false);
    }
  }

  @HostListener('window:resize')
  onViewportResize(): void {
    if (this.isTimeFilterOpen()) this.isTimeFilterOpen.set(false);
    if (this.isChartTypeOpen()) this.isChartTypeOpen.set(false);
    if (this.isExportDropdownOpen()) this.isExportDropdownOpen.set(false);
    if (this.isYAxisOpen()) this.isYAxisOpen.set(false);
  }
}
